/**
 * A video file's frame rate and how long its picture runs, read from its container: generated
 * clips are mostly 24 fps, and the editor's timeline runs at the rate of the clips on it.
 */

/** Rates video is made at; a measured rate this close to one of them is that rate. */
const COMMON_RATES = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60, 120];

/** A measured rate as the rate the file was made at (23.98 is 23.976), or null when it is not a usable rate. */
export const normalizeFrameRate = (rate: number): number | null => {
    if (!Number.isFinite(rate) || rate < 1 || rate > 240) return null;
    const nearest = COMMON_RATES.reduce((best, r) => (Math.abs(r - rate) < Math.abs(best - rate) ? r : best), COMMON_RATES[0]);
    // 0.05% apart: close enough to be the same rate, while 23.976 and 24 stay apart
    if (Math.abs(nearest - rate) / nearest < 0.0005) return nearest;
    return Math.round(rate * 1000) / 1000;
};

/** How many frames to look at: enough for a steady average, without reading a long file through. */
const SAMPLE_PACKETS = 120;

export interface VideoTiming {
    frameRate: number;
    /** How long the picture runs; the file can be longer when its sound runs on. Null when it cannot be told. */
    durationSec: number | null;
}

/** The frame rate of the video in `blob` and how long it runs, or null when it has no video or cannot be read. */
export const readVideoTiming = async (blob: Blob): Promise<VideoTiming | null> => {
    try {
        const { Input, BlobSource, ALL_FORMATS } = await import('mediabunny');
        const input = new Input({ source: new BlobSource(blob), formats: ALL_FORMATS });
        try {
            const track = await input.getPrimaryVideoTrack();
            if (!track) return null;
            const stats = await track.computePacketStats(SAMPLE_PACKETS);
            const frameRate = stats.packetCount > 1 ? normalizeFrameRate(stats.averagePacketRate) : null;
            if (!frameRate) return null;
            const [first, end] = await Promise.all([
                track.getFirstTimestamp().catch(() => 0),
                track.computeDuration().catch(() => NaN),
            ]);
            const durationSec = Number.isFinite(end) && end > 0 ? Math.round((end - Math.max(0, first)) * 1000) / 1000 : null;
            return { frameRate, durationSec };
        } finally {
            (input as unknown as { dispose?: () => void }).dispose?.();
        }
    } catch {
        return null;
    }
};
